import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from './api';
import { useAuth } from './AuthContext';
import './Dashboard.css';

const formatDateTime = (isoStr) => {
  if (!isoStr) return '-';
  try {
    const d = new Date(isoStr);
    if (isNaN(d.getTime())) return isoStr;
    return d.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return isoStr;
  }
};

const getRelativeTime = (isoStr) => {
  if (!isoStr) return '';
  try {
    const now = new Date();
    const d = new Date(isoStr);
    if (isNaN(d.getTime())) return '';
    const diffSec = Math.floor((now - d) / 1000);
    if (diffSec < 5) return 'только что';
    if (diffSec < 60) return `${diffSec} сек. назад`;
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin} мин. назад`;
    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24) return `${diffHours} ч. назад`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} дн. назад`;
  } catch {
    return '';
  }
};

const isRecent = (isoStr, maxMinutes = 15) => {
  if (!isoStr) return false;
  try {
    const d = new Date(isoStr);
    if (isNaN(d.getTime())) return false;
    const diffMin = (new Date() - d) / (1000 * 60);
    return diffMin >= 0 && diffMin <= maxMinutes;
  } catch {
    return false;
  }
};

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalObjects: 0,
    activeShifts: 0,
    totalBranches: 0,
  });
  const [objects, setObjects] = useState([]);
  const [branches, setBranches] = useState([]);
  const [currentShifts, setCurrentShifts] = useState([]);
  const [telemetry, setTelemetry] = useState(null);

  // Коллизии SMS
  const [conflicts, setConflicts] = useState([]);
  const [selectedConflict, setSelectedConflict] = useState(null);
  const [conflictAction, setConflictAction] = useState('replace_shift'); // 'replace_shift' | 'discard_sms' | 'recode'
  const [recodeTargetCode, setRecodeTargetCode] = useState('');
  const [conflictComment, setConflictComment] = useState('');
  const [submittingConflict, setSubmittingConflict] = useState(false);

  // Спорные / подозрительные SMS
  const [suspiciousList, setSuspiciousList] = useState([]);
  const [selectedSuspicious, setSelectedSuspicious] = useState(null);
  const [suspiciousAction, setSuspiciousAction] = useState('open_shift'); // 'open_shift' | 'recode' | 'discard_spam'
  const [suspiciousTargetCode, setSuspiciousTargetCode] = useState('');
  const [suspiciousEmployeeName, setSuspiciousEmployeeName] = useState('');
  const [suspiciousComment, setSuspiciousComment] = useState('');
  const [submittingSuspicious, setSubmittingSuspicious] = useState(false);


  // Фильтры и режимы отображения
  const [shiftViewMode, setShiftViewMode] = useState('inactive'); // 'inactive' | 'active' | 'all'
  const [closedFilter, setClosedFilter] = useState('all'); // 'all' | 'sms' | 'auto' | 'dispatcher'
  const [selectedBranchId, setSelectedBranchId] = useState('all'); // 'all' | number | 'none'
  const [searchQuery, setSearchQuery] = useState('');
  const [viewDensity, setViewDensity] = useState(() => {
    try {
      return localStorage.getItem('dashboard_view_density') || 'compact';
    } catch {
      return 'compact';
    }
  });
  const [collapsedBranches, setCollapsedBranches] = useState({}); // { [branchKey]: boolean }

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(new Date());

  const fetchData = useCallback(async (isManual = false) => {
    if (isManual) setRefreshing(true);
    try {
      const [objectsRes, branchesRes, shiftsRes, telemetryRes, conflictsRes, suspiciousRes] = await Promise.all([
        api.get('/objects', { params: { per_page: 1000, active_only: false } }),
        api.get('/branches/list', { params: { per_page: 100 } }).catch(() => ({ data: { items: [], total: 0 } })),
        api.get('/shifts', { params: { per_page: 1000 } }),
        api.get('/gateway/telemetry').catch((err) => {
          console.error('Error fetching telemetry:', err);
          return { data: null };
        }),
        api.get('/conflicts', { params: { status: 'pending' } }).catch((err) => {
          console.error('Error fetching conflicts:', err);
          return { data: [] };
        }),
        api.get('/suspicious', { params: { status: 'pending' } }).catch((err) => {
          console.error('Error fetching suspicious SMS:', err);
          return { data: [] };
        }),
      ]);

      const objItems = objectsRes.data?.items || [];
      const branchItems = branchesRes.data?.items || [];
      const shiftItems = shiftsRes.data?.items || [];
      const conflictItems = Array.isArray(conflictsRes.data) ? conflictsRes.data : [];
      const suspiciousItems = Array.isArray(suspiciousRes.data) ? suspiciousRes.data : [];

      setObjects(objItems);
      setBranches(branchItems);
      setCurrentShifts(shiftItems);
      setTelemetry(telemetryRes.data || null);
      setConflicts(conflictItems);
      setSuspiciousList(suspiciousItems);
      setLastRefreshedAt(new Date());

      const totalBranchesCount = branchItems.length > 0
        ? (branchesRes.data.total || branchItems.length)
        : new Set(objItems.map((o) => o.branch_name).filter(Boolean)).size;

      setStats({
        totalObjects: objectsRes.data?.total !== undefined ? objectsRes.data.total : objItems.length,
        activeShifts: shiftsRes.data?.total !== undefined ? shiftsRes.data.total : shiftItems.length,
        totalBranches: totalBranchesCount,
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
      if (isManual) setRefreshing(false);
    }
  }, []);

  const conflictByObjectId = useMemo(() => {
    const map = {};
    for (const c of conflicts) {
      if (!map[c.object_id]) {
        map[c.object_id] = c;
      }
    }
    return map;
  }, [conflicts]);

  const recodeTargetInfo = useMemo(() => {
    if (conflictAction !== 'recode') return null;
    const trimmed = (recodeTargetCode || '').trim();
    if (!trimmed) return null;

    const targetObj = objects.find(
      (o) => String(o.code).toLowerCase() === trimmed.toLowerCase()
    );
    if (!targetObj) {
      return {
        found: false,
        code: trimmed,
      };
    }

    const activeShift = currentShifts.find(
      (s) => s.object_id === targetObj.id && !s.ended_at
    );

    return {
      found: true,
      code: targetObj.code,
      object: targetObj,
      activeShift: activeShift || null,
    };
  }, [conflictAction, recodeTargetCode, objects, currentShifts]);

  const handleResolveConflict = async () => {
    if (!selectedConflict) return;
    if (conflictAction === 'recode') {
      const trimmed = (recodeTargetCode || '').trim();
      if (!trimmed) {
        alert('Пожалуйста, укажите код объекта для перекодирования');
        return;
      }
      if (recodeTargetInfo && !recodeTargetInfo.found) {
        alert(`Объект с кодом "${trimmed}" не найден в справочнике объектов. Проверьте правильность ввода.`);
        return;
      }
      if (recodeTargetInfo && recodeTargetInfo.activeShift) {
        const confirmMsg =
          `⚠️ ВНИМАНИЕ!\n\n` +
          `На объекте [${recodeTargetInfo.code}] «${recodeTargetInfo.object.name}» уже открыта активная смена!\n` +
          `• Сотрудник на объекте: ${recodeTargetInfo.activeShift.employee_name}\n` +
          `• Заступил: ${formatDateTime(recodeTargetInfo.activeShift.started_at)}\n\n` +
          `При подтверждении текущая смена ${recodeTargetInfo.activeShift.employee_name} будет ЗАВЕРШЕНА, и откроется новая смена для ${selectedConflict.employee_name}.\n\n` +
          `Вы подтверждаете переназначение?`;
        if (!window.confirm(confirmMsg)) {
          return;
        }
      }
    }
    setSubmittingConflict(true);
    try {
      await api.post(`/conflicts/${selectedConflict.id}/resolve`, {
        action: conflictAction,
        new_object_code: conflictAction === 'recode' ? recodeTargetCode.trim() : null,
        comment: conflictComment.trim() || null,
      });
      setSelectedConflict(null);
      setRecodeTargetCode('');
      setConflictComment('');
      setConflictAction('replace_shift');
      await fetchData();
    } catch (err) {
      console.error('Error resolving conflict:', err);
      alert(err.response?.data?.detail || 'Ошибка при разрешении коллизии');
    } finally {
      setSubmittingConflict(false);
    }
  };

  const suspiciousTargetInfo = useMemo(() => {
    if (!selectedSuspicious) return null;
    const effectiveCode = (
      suspiciousAction === 'recode'
        ? suspiciousTargetCode
        : (suspiciousTargetCode || selectedSuspicious.extracted_code || selectedSuspicious.suggested_object?.code || '')
    ).trim();

    if (!effectiveCode) return null;

    const targetObj = objects.find(
      (o) => String(o.code).toLowerCase() === effectiveCode.toLowerCase()
    );
    if (!targetObj) {
      return {
        found: false,
        code: effectiveCode,
      };
    }

    const activeShift = currentShifts.find(
      (s) => s.object_id === targetObj.id && !s.ended_at
    );

    return {
      found: true,
      code: targetObj.code,
      object: targetObj,
      activeShift: activeShift || null,
    };
  }, [selectedSuspicious, suspiciousAction, suspiciousTargetCode, objects, currentShifts]);

  const handleResolveSuspicious = async () => {
    if (!selectedSuspicious) return;

    if (suspiciousAction !== 'discard_spam') {
      const codeToUse = (
        suspiciousAction === 'recode'
          ? suspiciousTargetCode
          : (suspiciousTargetCode || selectedSuspicious.extracted_code || selectedSuspicious.suggested_object?.code || '')
      ).trim();

      if (!codeToUse) {
        alert('Пожалуйста, укажите код объекта для открытия смены');
        return;
      }
      if (suspiciousTargetInfo && !suspiciousTargetInfo.found) {
        alert(`Объект с кодом "${codeToUse}" не найден в справочнике объектов.`);
        return;
      }
      if (suspiciousTargetInfo && suspiciousTargetInfo.activeShift) {
        const confirmMsg =
          `⚠️ ВНИМАНИЕ!\n\n` +
          `На объекте [${suspiciousTargetInfo.code}] «${suspiciousTargetInfo.object.name}» уже открыта смена сотрудника ${suspiciousTargetInfo.activeShift.employee_name}.\n\n` +
          `При подтверждении текущая смена будет ЗАВЕРШЕНА, и откроется новая смена для ${suspiciousEmployeeName || selectedSuspicious.extracted_name || 'сотрудника'}.\n\n` +
          `Вы подтверждаете открытие смены?`;
        if (!window.confirm(confirmMsg)) {
          return;
        }
      }
    }

    setSubmittingSuspicious(true);
    try {
      await api.post(`/suspicious/${selectedSuspicious.id}/resolve`, {
        action: suspiciousAction,
        target_object_code: (
          suspiciousAction === 'recode'
            ? suspiciousTargetCode
            : (suspiciousTargetCode || selectedSuspicious.extracted_code || selectedSuspicious.suggested_object?.code || '')
        ).trim() || null,
        employee_name: suspiciousEmployeeName.trim() || null,
        comment: suspiciousComment.trim() || null,
      });
      setSelectedSuspicious(null);
      setSuspiciousTargetCode('');
      setSuspiciousEmployeeName('');
      setSuspiciousComment('');
      setSuspiciousAction('open_shift');
      await fetchData();
    } catch (err) {
      console.error('Error resolving suspicious SMS:', err);
      alert(err.response?.data?.detail || 'Ошибка при обработке спорного SMS');
    } finally {
      setSubmittingSuspicious(false);
    }
  };

  useEffect(() => {
    fetchData();
    const intervalId = setInterval(() => {
      fetchData();
    }, 30000);
    return () => clearInterval(intervalId);
  }, [fetchData]);

  const branchMap = useMemo(() => {
    const map = {};
    for (const b of branches) {
      map[b.id] = b;
    }
    return map;
  }, [branches]);

  // Маппинг активных смен по object_id
  const activeShiftByObjectId = useMemo(() => {
    const map = {};
    for (const s of currentShifts) {
      if (s.is_active || s.ended_at === null) {
        map[s.object_id] = s;
      }
    }
    return map;
  }, [currentShifts]);

  // Маппинг последней завершенной смены по object_id (для истории закрытия на пультах)
  const latestClosedShiftByObjectId = useMemo(() => {
    const map = {};
    for (const s of currentShifts) {
      if (!s.is_active && s.ended_at !== null) {
        if (!map[s.object_id] || new Date(s.ended_at) > new Date(map[s.object_id].ended_at)) {
          map[s.object_id] = s;
        }
      }
    }
    return map;
  }, [currentShifts]);

  // Сводные метрики филиалов (отображаются строго «на смене» и «не на смене»)
  const branchMetrics = useMemo(() => {
    const groups = {};
    for (const b of branches) {
      groups[b.id] = {
        id: b.id,
        name: b.name,
        code: b.code,
        sort_order: b.sort_order || 0,
        activeShifts: 0,
        inactiveShifts: 0,
        totalObjects: 0,
      };
    }

    let noBranchActive = 0;
    let noBranchInactive = 0;
    let noBranchTotal = 0;

    let overallActive = 0;
    let overallInactive = 0;

    for (const obj of objects) {
      if (!obj.is_active) continue;
      const hasShift = !!activeShiftByObjectId[obj.id];
      if (hasShift) {
        overallActive++;
      } else {
        overallInactive++;
      }

      if (obj.branch_id && groups[obj.branch_id]) {
        groups[obj.branch_id].totalObjects++;
        if (hasShift) {
          groups[obj.branch_id].activeShifts++;
        } else {
          groups[obj.branch_id].inactiveShifts++;
        }
      } else {
        noBranchTotal++;
        if (hasShift) {
          noBranchActive++;
        } else {
          noBranchInactive++;
        }
      }
    }

    const sorted = Object.values(groups).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    if (noBranchTotal > 0) {
      sorted.push({
        id: 'none',
        name: 'Без филиала',
        code: null,
        activeShifts: noBranchActive,
        inactiveShifts: noBranchInactive,
        totalObjects: noBranchTotal,
      });
    }

    return {
      items: sorted,
      overall: {
        activeShifts: overallActive,
        inactiveShifts: overallInactive,
        totalObjects: overallActive + overallInactive,
      },
    };
  }, [branches, objects, activeShiftByObjectId]);

  // Статистика в текущей области видимости (с учетом выбранного филиала и поиска)
  const currentScopeStats = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    let activeShifts = 0;
    let inactiveShifts = 0;

    for (const obj of objects) {
      if (!obj.is_active) continue;

      // Фильтр по выбранному филиалу
      if (selectedBranchId !== 'all') {
        if (selectedBranchId === 'none') {
          if (obj.branch_id) continue;
        } else if (obj.branch_id !== selectedBranchId) {
          continue;
        }
      }

      const shift = activeShiftByObjectId[obj.id] || null;

      // Фильтр по строке поиска
      if (query) {
        const matchCode = obj.code && obj.code.toLowerCase().includes(query);
        const matchName = obj.name && obj.name.toLowerCase().includes(query);
        const matchAddress = obj.address && obj.address.toLowerCase().includes(query);
        const matchEmployee = shift?.employee_name && shift.employee_name.toLowerCase().includes(query);
        if (!matchCode && !matchName && !matchAddress && !matchEmployee) {
          continue;
        }
      }

      if (shift) {
        activeShifts++;
      } else {
        inactiveShifts++;
      }
    }

    return {
      activeShifts,
      inactiveShifts,
      totalObjects: activeShifts + inactiveShifts,
    };
  }, [objects, selectedBranchId, searchQuery, activeShiftByObjectId]);

  // Фильтрация и группировка постов
  const groupedData = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const groups = {};

    for (const obj of objects) {
      if (!obj.is_active) continue;

      const shift = activeShiftByObjectId[obj.id] || null;
      const latestClosedShift = latestClosedShiftByObjectId[obj.id] || null;
      const hasActiveShift = !!shift;

      // 1. Фильтр по статусу смены
      if (shiftViewMode === 'inactive' && hasActiveShift) continue;
      if (shiftViewMode === 'active' && !hasActiveShift) continue;

      // 1.1 Фильтр закрытых смен (если смена не активна и выбран closedFilter)
      if (!hasActiveShift && closedFilter !== 'all') {
        if (!latestClosedShift || latestClosedShift.close_type !== closedFilter) {
          continue;
        }
      }

      // 2. Фильтр по выбранному филиалу
      const bId = obj.branch_id || 'none';
      if (selectedBranchId !== 'all') {
        if (selectedBranchId === 'none') {
          if (obj.branch_id) continue;
        } else if (obj.branch_id !== selectedBranchId) {
          continue;
        }
      }

      // 3. Живой поиск (по коду, названию, адресу, сотруднику на смене)
      if (query) {
        const matchCode = obj.code && obj.code.toLowerCase().includes(query);
        const matchName = obj.name && obj.name.toLowerCase().includes(query);
        const matchAddress = obj.address && obj.address.toLowerCase().includes(query);
        const matchEmployee = shift?.employee_name && shift.employee_name.toLowerCase().includes(query);
        if (!matchCode && !matchName && !matchAddress && !matchEmployee) {
          continue;
        }
      }

      // Группировка
      if (!groups[bId]) {
        const branch = obj.branch_id ? branchMap[obj.branch_id] : null;
        groups[bId] = {
          branchId: obj.branch_id || 'none',
          branchName: obj.branch_name || 'Без филиала',
          branchCode: branch?.code || null,
          sortOrder: branch?.sort_order ?? (obj.branch_id ? 1000 : 9999),
          directorName: obj.branch_manager_name || branch?.manager_name || null,
          directorPhone: obj.branch_manager_phone || branch?.manager_phone || null,
          activeCount: 0,
          inactiveCount: 0,
          items: [],
        };
      }

      if (hasActiveShift) {
        groups[bId].activeCount++;
      } else {
        groups[bId].inactiveCount++;
      }

      groups[bId].items.push({ obj, shift, latestClosedShift });
    }

    // Сортировка постов внутри филиала по коду
    for (const g of Object.values(groups)) {
      g.items.sort((a, b) => {
        const codeA = a.obj.code || '';
        const codeB = b.obj.code || '';
        if (codeA && codeB) return codeA.localeCompare(codeB, undefined, { numeric: true });
        return a.obj.name.localeCompare(b.obj.name);
      });
    }

    // Сортировка филиалов по установленному sort_order
    return Object.values(groups).sort((a, b) => {
      if (a.branchId === 'none') return 1;
      if (b.branchId === 'none') return -1;
      if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
      return a.branchName.localeCompare(b.branchName);
    });
  }, [objects, activeShiftByObjectId, latestClosedShiftByObjectId, shiftViewMode, closedFilter, selectedBranchId, searchQuery, branchMap]);

  // Управление раскрытием/сворачиванием филиалов
  const isBranchCollapsed = useCallback((branchKey) => {
    // Если есть поисковый запрос или выбран конкретный филиал — разворачиваем автоматически
    if (searchQuery.trim().length > 0) return false;
    if (selectedBranchId !== 'all') return false;
    // По умолчанию — свернуто (true), пока пользователь не развернул
    return collapsedBranches[branchKey] ?? true;
  }, [searchQuery, selectedBranchId, collapsedBranches]);

  const toggleBranchCollapse = (branchKey) => {
    const current = isBranchCollapsed(branchKey);
    setCollapsedBranches((prev) => ({
      ...prev,
      [branchKey]: !current,
    }));
  };

  const handleExpandAll = () => {
    const next = {};
    for (const g of groupedData) {
      next[g.branchId] = false;
    }
    setCollapsedBranches(next);
  };

  const handleCollapseAll = () => {
    const next = {};
    for (const g of groupedData) {
      next[g.branchId] = true;
    }
    setCollapsedBranches(next);
  };

  const handleSelectBranch = (branchId) => {
    if (selectedBranchId === branchId) {
      setSelectedBranchId('all');
    } else {
      setSelectedBranchId(branchId);
    }
  };

  const handleDensityChange = (density) => {
    setViewDensity(density);
    try {
      localStorage.setItem('dashboard_view_density', density);
    } catch {
      // ignore
    }
  };

  if (loading) {
    return <div className="loading">Загрузка данных пульта диспетчера...</div>;
  }

  const routerOnline = isRecent(telemetry?.last_router_sync_at);

  return (
    <div className="dashboard">
      {/* 1. Верхняя панель: приветствие, общий счетчик и кнопка обновления */}
      <div className="dashboard-topbar">
        <div className="dashboard-welcome">
          <h2>Добро пожаловать, {user?.full_name || user?.username}!</h2>
          <span className="dashboard-updated">
            Обновлено: {lastRefreshedAt.toLocaleTimeString('ru-RU')}
          </span>
        </div>

        {/* Карточка «Все филиалы» / Всего постов охраны, перенесённая в шапку */}
        <div
          className={`topbar-all-branches-card ${selectedBranchId === 'all' ? 'selected' : ''}`}
          onClick={() => setSelectedBranchId('all')}
          title="Показать все филиалы (нажмите для сброса фильтра)"
          role="button"
          tabIndex={0}
        >
          <div className="topbar-branch-main">
            <div className="topbar-branch-title">
              <span className="topbar-branch-icon">🏢</span>
              <span className="topbar-branch-name">Все филиалы</span>
              {selectedBranchId === 'all' && (
                <span className="topbar-branch-active-tag">Активен</span>
              )}
            </div>
            <div className="topbar-branch-total">
              <span className="topbar-stat-label">Всего постов охраны:</span>
              <strong className="topbar-stat-value">{stats.totalObjects}</strong>
              {stats.totalBranches > 0 && (
                <span className="topbar-stat-sub">в {stats.totalBranches} филиалах</span>
              )}
            </div>
          </div>

          <div className="topbar-branch-divider"></div>

          <div className="topbar-branch-metrics">
            <div className="topbar-branch-metric active-metric" title="На смене по всем филиалам">
              <span className="metric-dot dot-green"></span>
              <span className="metric-label">На смене:</span>
              <strong className="metric-value">{branchMetrics.overall.activeShifts}</strong>
            </div>
            <div className="topbar-branch-metric inactive-metric" title="Не на смене по всем филиалам">
              <span className="metric-dot dot-red"></span>
              <span className="metric-label">Не на смене:</span>
              <strong className="metric-value">{branchMetrics.overall.inactiveShifts}</strong>
            </div>
          </div>
        </div>

        <button
          className="btn-refresh"
          onClick={() => fetchData(true)}
          disabled={refreshing}
          title="Обновить данные сейчас"
        >
          {refreshing ? 'Обновление...' : '🔄 Обновить'}
        </button>
      </div>

      {/* Баннер неразрешённых инцидентов SMS (Коллизии и Спорные) */}
      {(conflicts.length > 0 || suspiciousList.length > 0) && (
        <div className="conflicts-alert-banner">
          <div className="conflicts-alert-content">
            <span className="conflicts-alert-icon">⚠️</span>
            <div>
              <strong>
                Внимание:
                {conflicts.length > 0 && ` коллизий SMS: ${conflicts.length}`}
                {conflicts.length > 0 && suspiciousList.length > 0 && ' • '}
                {suspiciousList.length > 0 && ` спорных SMS: ${suspiciousList.length}`}!
              </strong>
              <div className="conflicts-alert-desc">
                Поступили входящие сообщения, требующие ручного рассмотрения (конкурирующие SMS или ошибки формата/кода).
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
            {conflicts.length > 0 && (
              <button
                className="btn-resolve-conflicts"
                onClick={() => {
                  setSelectedConflict(conflicts[0]);
                  setConflictAction('replace_shift');
                  setRecodeTargetCode('');
                  setConflictComment('');
                }}
              >
                Коллизии ({conflicts.length})
              </button>
            )}
            {suspiciousList.length > 0 && (
              <button
                className="btn-resolve-conflicts btn-resolve-suspicious"
                onClick={() => {
                  const first = suspiciousList[0];
                  setSelectedSuspicious(first);
                  setSuspiciousAction('open_shift');
                  setSuspiciousTargetCode(first.extracted_code || first.suggested_object?.code || '');
                  setSuspiciousEmployeeName(first.extracted_name || '');
                  setSuspiciousComment('');
                }}
              >
                📩 Спорные SMS ({suspiciousList.length})
              </button>
            )}
          </div>
        </div>
      )}

      {/* 2. Карточки филиалов (наверху): только «🟢 На смене» и «🔴 Не на смене», кликабельны для фильтрации */}
      <div className="branch-summary-section">
        <div className="branch-section-header">
          <h3 className="section-title">Филиалы (нажмите для фильтрации)</h3>
          {selectedBranchId !== 'all' && (
            <button
              className="btn-reset-branch-filter"
              onClick={() => setSelectedBranchId('all')}
            >
              ✕ Сбросить фильтр филиала
            </button>
          )}
        </div>

        <div className="branch-summary-grid">
          {/* Карточки по каждому филиалу */}
          {branchMetrics.items.map((b) => {
            const isSelected = selectedBranchId === b.id;
            return (
              <div
                key={b.id || b.name}
                className={`branch-card ${isSelected ? 'selected' : ''}`}
                onClick={() => handleSelectBranch(b.id)}
                title={`Нажмите, чтобы отфильтровать по ${b.name}`}
              >
                <div className="branch-card-header">
                  <span className="branch-card-icon">🏢</span>
                  <span className="branch-card-name" title={b.name}>{b.name}</span>
                </div>
                <div className="branch-card-metrics">
                  <div className="branch-card-metric active-metric">
                    <span className="metric-dot dot-green"></span>
                    <span className="metric-label">На смене:</span>
                    <strong className="metric-value" style={{ color: b.activeShifts > 0 ? '#16a34a' : '#94a3b8' }}>
                      {b.activeShifts}
                    </strong>
                  </div>
                  <div className="branch-card-metric inactive-metric">
                    <span className="metric-dot dot-red"></span>
                    <span className="metric-label">Не на смене:</span>
                    <strong className="metric-value" style={{ color: b.inactiveShifts > 0 ? '#dc2626' : '#94a3b8' }}>
                      {b.inactiveShifts}
                    </strong>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 3. Рабочая панель мониторинга смен: фильтры, поиск, аккордеон и режим отображения */}
      <div className="section active-shifts-section">
        {/* Панель управления мониторингом */}
        <div className="monitor-toolbar">
          {/* Вкладки статуса смен */}
          <div className="shifts-tabs-container" style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            <div className="shifts-tabs">
              <button
                className={`shift-tab-btn ${shiftViewMode === 'inactive' ? 'active alert' : ''}`}
                onClick={() => setShiftViewMode('inactive')}
              >
                🔴 Не на смене ({currentScopeStats.inactiveShifts})
              </button>
              <button
                className={`shift-tab-btn ${shiftViewMode === 'active' ? 'active success' : ''}`}
                onClick={() => setShiftViewMode('active')}
              >
                🟢 На смене ({currentScopeStats.activeShifts})
              </button>
              <button
                className={`shift-tab-btn ${shiftViewMode === 'all' ? 'active neutral' : ''}`}
                onClick={() => setShiftViewMode('all')}
              >
                Все посты ({currentScopeStats.totalObjects})
              </button>
            </div>

            {shiftViewMode !== 'active' && (
              <div className="closed-shift-filter-chips" style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600, marginRight: '0.2rem' }}>Закрытие:</span>
                <button
                  type="button"
                  className={`closed-filter-chip ${closedFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setClosedFilter('all')}
                >
                  Все
                </button>
                <button
                  type="button"
                  className={`closed-filter-chip ${closedFilter === 'sms' ? 'active' : ''}`}
                  onClick={() => setClosedFilter('sms')}
                  title="Закрытые по входящему SMS от охранника"
                >
                  📱 По SMS
                </button>
                <button
                  type="button"
                  className={`closed-filter-chip ${closedFilter === 'auto' ? 'active' : ''}`}
                  onClick={() => setClosedFilter('auto')}
                  title="Закрытые автоматически по графику (21:00)"
                >
                  ⏰ Авто (21:00)
                </button>
                <button
                  type="button"
                  className={`closed-filter-chip ${closedFilter === 'dispatcher' ? 'active' : ''}`}
                  onClick={() => setClosedFilter('dispatcher')}
                  title="Закрытые диспетчером"
                >
                  👤 Диспетчером
                </button>
              </div>
            )}
          </div>

          {/* Строка живого поиска */}
          <div className="search-box-wrapper">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              className="search-input"
              placeholder="Поиск по коду (0201), объекту, адресу, охраннику..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                className="search-clear-btn"
                onClick={() => setSearchQuery('')}
                title="Очистить поиск"
              >
                ✕
              </button>
            )}
          </div>

          {/* Переключатель вида (Таблица / Пульт) и управление аккордеоном */}
          <div className="toolbar-actions">
            <div className="view-density-toggle">
              <button
                className={`density-btn ${viewDensity === 'table' ? 'active' : ''}`}
                onClick={() => handleDensityChange('table')}
                title="Подробный вид: таблица"
              >
                📋 Таблица
              </button>
              <button
                className={`density-btn ${viewDensity === 'compact' ? 'active' : ''}`}
                onClick={() => handleDensityChange('compact')}
                title="Компактный вид: пульт плитки"
              >
                🎛️ Пульт
              </button>
            </div>

            <div className="accordion-controls">
              <button
                className="btn-accordion-control"
                onClick={handleExpandAll}
                title="Развернуть все филиалы"
              >
                Развернуть все
              </button>
              <button
                className="btn-accordion-control"
                onClick={handleCollapseAll}
                title="Свернуть все филиалы"
              >
                Свернуть все
              </button>
            </div>
          </div>
        </div>

        {/* Список филиалов и постов (Аккордеон) */}
        {groupedData.length > 0 ? (
          <div className="branches-accordion">
            {groupedData.map((group) => {
              const collapsed = isBranchCollapsed(group.branchId);

              return (
                <div key={`group-${group.branchId}`} className="branch-accordion-item">
                  {/* Заголовок группы филиала (кликабельный) */}
                  <div
                    className={`branch-accordion-header ${collapsed ? 'collapsed' : 'expanded'}`}
                    onClick={() => toggleBranchCollapse(group.branchId)}
                  >
                    <div className="branch-header-left">
                      <span className="branch-chevron">{collapsed ? '▶' : '▼'}</span>
                      <span className="branch-header-icon">🏢</span>
                      <strong className="branch-header-name">{group.branchName}</strong>

                      {/* Счетчики внутри филиала */}
                      <div className="branch-header-counts">
                        <span className="branch-count-pill green" title="Постов на смене">
                          🟢 {group.activeCount}
                        </span>
                        <span className="branch-count-pill red" title="Постов не на смене">
                          🔴 {group.inactiveCount}
                        </span>
                      </div>
                    </div>

                    {/* Информация о директоре филиала */}
                    <div className="branch-header-right" onClick={(e) => e.stopPropagation()}>
                      {group.directorName ? (
                        <span className="branch-director-label">
                          Директор: <strong>{group.directorName}</strong>
                        </span>
                      ) : (
                        <span className="branch-director-empty">Директор не назначен</span>
                      )}

                      {group.directorPhone && (
                        <a
                          href={`tel:${group.directorPhone}`}
                          className="director-phone-link"
                          title="Позвонить директору филиала"
                        >
                          📞 {group.directorPhone}
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Тело группы: отображается, только если филиал развернут */}
                  {!collapsed && (
                    <div className="branch-accordion-body">
                      {viewDensity === 'table' ? (
                        /* Подробный табличный вид */
                        <div className="table-container">
                          <table className="table table-branch-view">
                            <thead>
                              <tr>
                                <th style={{ width: '110px' }}>Код поста</th>
                                <th>Объект охраны</th>
                                <th>График / Сотрудник</th>
                                <th style={{ width: '170px' }}>Статус смены</th>
                              </tr>
                            </thead>
                            <tbody>
                              {group.items.map(({ obj, shift, latestClosedShift }) => {
                                const hasShift = !!shift;
                                return (
                                  <tr
                                    key={obj.id}
                                    className={`shift-row ${hasShift ? 'shift-active-row' : 'shift-inactive-row'}`}
                                  >
                                    <td className="post-code-cell">
                                      <span className="post-code-badge">
                                        {obj.code || `#${obj.id}`}
                                      </span>
                                    </td>
                                    <td>
                                      <div className="object-name-main">{obj.name}</div>
                                      {obj.address && (
                                        <div className="object-address-sub">{obj.address}</div>
                                      )}
                                    </td>
                                    <td>
                                      {hasShift ? (
                                        <div className="shift-employee-info">
                                          <strong>👤 {shift.employee_name}</strong>
                                          <div className="shift-started-sub">
                                            с {new Date(shift.started_at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })} ({formatDateTime(shift.started_at)})
                                          </div>
                                          {shift.notes && (
                                            <div className="shift-notes-sub" title={shift.notes} style={{ fontSize: '0.78rem', color: '#475569', marginTop: '0.2rem', maxWidth: '320px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                              📝 {shift.notes}
                                            </div>
                                          )}
                                        </div>
                                      ) : (
                                        <div className="inactive-post-schedule">
                                          <span className="shift-schedule-badge">
                                            ⏰ {obj.shift_start || '06:00'} – {obj.shift_end || '21:00'}
                                          </span>
                                          {latestClosedShift && latestClosedShift.ended_at && (
                                            <div className="closed-shift-detail" style={{ marginTop: '0.3rem', fontSize: '0.78rem' }}>
                                              <span className={`close-type-badge close-type-${latestClosedShift.close_type || 'default'}`}>
                                                {latestClosedShift.close_type === 'sms' && '📱 По SMS'}
                                                {latestClosedShift.close_type === 'auto' && '⏰ Авто (21:00)'}
                                                {latestClosedShift.close_type === 'dispatcher' && '👤 Диспетчером'}
                                                {!latestClosedShift.close_type && 'Завершена'}
                                              </span>
                                              <div style={{ color: '#64748b', fontSize: '0.74rem', marginTop: '0.15rem' }}>
                                                {formatDateTime(latestClosedShift.ended_at)}
                                                {latestClosedShift.close_type === 'sms' && (
                                                  <span> • Закрыл: <strong>{latestClosedShift.ended_by_name || latestClosedShift.employee_name}</strong></span>
                                                )}
                                                {latestClosedShift.close_type === 'dispatcher' && latestClosedShift.ended_by_name && (
                                                  <span> • <strong>{latestClosedShift.ended_by_name}</strong></span>
                                                )}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </td>
                                    <td>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                        {hasShift ? (
                                          <span className="status-badge-active">
                                            🟢 На смене
                                          </span>
                                        ) : (
                                          <span className="status-badge-inactive">
                                            🔴 Не на смене
                                          </span>
                                        )}
                                        {conflictByObjectId[obj.id] && (
                                          <button
                                            type="button"
                                            className="table-conflict-badge"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setSelectedConflict(conflictByObjectId[obj.id]);
                                              setConflictAction('replace_shift');
                                              setRecodeTargetCode('');
                                              setConflictComment('');
                                            }}
                                            title="Поступило новое SMS на открытую смену — требуется решение"
                                          >
                                            ⚠️ Коллизия SMS
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        /* Сверхкомпактный вид «Пульт» (сетка плашек) */
                        <div className="post-chips-grid">
                          {group.items.map(({ obj, shift, latestClosedShift }) => {
                            const hasShift = !!shift;
                            const objConflict = conflictByObjectId[obj.id];
                            const closeInfo = latestClosedShift && latestClosedShift.ended_at
                              ? `\n\nСмена не заступила\nПоследняя смена: ${formatDateTime(latestClosedShift.ended_at)} (${latestClosedShift.close_type === 'sms' ? 'по SMS: ' + (latestClosedShift.ended_by_name || latestClosedShift.employee_name) : latestClosedShift.close_type === 'auto' ? 'автоматически 21:00' : 'диспетчером' + (latestClosedShift.ended_by_name ? ' ' + latestClosedShift.ended_by_name : '')})`
                              : '\n\nСмена не заступила';
                            const tooltip = `Код: ${obj.code || '#' + obj.id}\nОбъект: ${obj.name}\nАдрес: ${obj.address || 'Адрес не указан'}\nГрафик: ${obj.shift_start || '06:00'} - ${obj.shift_end || '21:00'}${hasShift ? `\n\nСотрудник на смене: ${shift.employee_name}\nЗаступил: ${formatDateTime(shift.started_at)}${shift.notes ? '\nПримечание: ' + shift.notes : ''}` : closeInfo}${objConflict ? `\n\n⚠️ ВНИМАНИЕ: Коллизия SMS от ${objConflict.employee_name} (${formatDateTime(objConflict.sms_time)})` : ''}`;

                            return (
                              <div
                                key={obj.id}
                                className={`post-chip ${hasShift ? 'active' : 'inactive'} ${objConflict ? 'has-conflict' : ''}`}
                                title={tooltip}
                                onClick={objConflict ? () => {
                                  setSelectedConflict(objConflict);
                                  setConflictAction('replace_shift');
                                  setRecodeTargetCode('');
                                  setConflictComment('');
                                } : undefined}
                              >
                                <span className="post-chip-code">{obj.code || `#${obj.id}`}</span>
                                <span className="post-chip-name">{obj.name}</span>
                                <span className="post-chip-status">
                                  {hasShift ? (
                                    <span className="chip-status-text" title={shift.employee_name}>
                                      👤 {shift.employee_name}
                                    </span>
                                  ) : (
                                    <span className="chip-status-text alert" title={latestClosedShift ? `Закрыта ${formatDateTime(latestClosedShift.ended_at)}` : 'Без смены'}>
                                      {latestClosedShift?.close_type === 'sms' && '📱 SMS'}
                                      {latestClosedShift?.close_type === 'auto' && '⏰ Авто'}
                                      {latestClosedShift?.close_type === 'dispatcher' && '👤 Дисп.'}
                                      {!latestClosedShift?.close_type && 'Без смены'}
                                    </span>
                                  )}
                                  {objConflict && (
                                    <span
                                      className="chip-conflict-tag"
                                      title="Коллизия SMS: нажмите для разрешения"
                                    >
                                      ⚠️ Коллизия
                                    </span>
                                  )}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="empty-results-box">
            {searchQuery ? (
              <>
                <div className="empty-icon">🔍</div>
                <div className="empty-text">
                  Ничего не найдено по запросу «<strong>{searchQuery}</strong>»
                </div>
                <button
                  className="btn-clear-search-filter"
                  onClick={() => setSearchQuery('')}
                >
                  Очистить поиск
                </button>
              </>
            ) : shiftViewMode === 'active' ? (
              <div className="empty-text-simple">
                На данный момент нет активных смен.
              </div>
            ) : shiftViewMode === 'inactive' ? (
              <div className="empty-text-success">
                🎉 Все посты охраны заступили на смену! Неактивных постов нет.
              </div>
            ) : (
              <div className="empty-text-simple">
                Посты не найдены по выбранным условиям фильтра.
              </div>
            )}
          </div>
        )}
      </div>

      {/* 4. В самом низу: информация о синхронизации и SMS */}
      <div className="telemetry-section">
        <h3 style={{ margin: '0 0 1rem', color: '#1e293b' }}>Информация о синхронизации и SMS</h3>
        <div className="telemetry-grid">
          {/* Карточка SMS */}
          <div className="telemetry-card">
            <div className="telemetry-header">
              <div className="telemetry-header-left">
                <span className="telemetry-icon">📱</span>
                <div>
                  <div className="telemetry-title">Последнее SMS-сообщение</div>
                  <div className="telemetry-subtitle">Поступление данных со смен</div>
                </div>
              </div>
              {telemetry?.last_sms_at && (
                <span className="telemetry-status-pill sms">
                  {getRelativeTime(telemetry.last_sms_at)}
                </span>
              )}
            </div>
            <div className="telemetry-body">
              {telemetry?.last_sms_at ? (
                <>
                  <div className="telemetry-time-row">
                    <span className="telemetry-time-label">Время:</span>
                    <strong className="telemetry-time-value">
                      {formatDateTime(telemetry.last_sms_at)}
                    </strong>
                  </div>
                  <div className="telemetry-tags">
                    {telemetry.last_sms_code && (
                      <span className="telemetry-tag code" title="Код объекта">
                        Пост: <strong>{telemetry.last_sms_code}</strong>
                      </span>
                    )}
                    {telemetry.last_sms_name && (
                      <span className="telemetry-tag name" title="Сотрудник">
                        👤 {telemetry.last_sms_name}
                      </span>
                    )}
                    {telemetry.last_sms_sender && (
                      <span className="telemetry-tag sender" title="Номер телефона отправителя">
                        📞 {telemetry.last_sms_sender}
                      </span>
                    )}
                  </div>
                  {telemetry.last_sms_raw && (
                    <div className="telemetry-raw-box" title="Текст исходного SMS">
                      «{telemetry.last_sms_raw}»
                    </div>
                  )}
                </>
              ) : (
                <div className="telemetry-empty">SMS-сообщений еще не поступало</div>
              )}
            </div>
          </div>

          {/* Карточка Синхронизации с роутером */}
          <div className="telemetry-card">
            <div className="telemetry-header">
              <div className="telemetry-header-left">
                <span className="telemetry-icon">🔄</span>
                <div>
                  <div className="telemetry-title">Синхронизация с роутером</div>
                  <div className="telemetry-subtitle">Роутер Z8102AX (SMS-шлюз)</div>
                </div>
              </div>
              {telemetry?.last_router_sync_at ? (
                <span className={`telemetry-status-pill ${routerOnline ? 'online' : 'stale'}`}>
                  <span className="status-bullet"></span>
                  {routerOnline ? 'На связи' : 'Ожидание'}
                </span>
              ) : (
                <span className="telemetry-status-pill empty">Нет связи</span>
              )}
            </div>
            <div className="telemetry-body">
              {telemetry?.last_router_sync_at ? (
                <>
                  <div className="telemetry-time-row">
                    <span className="telemetry-time-label">Синхронизировано:</span>
                    <strong className="telemetry-time-value">
                      {formatDateTime(telemetry.last_router_sync_at)}
                    </strong>
                    <span className="telemetry-time-ago">
                      ({getRelativeTime(telemetry.last_router_sync_at)})
                    </span>
                  </div>
                  <div className="telemetry-tags">
                    <span className="telemetry-tag ip" title="IP-адрес шлюза">
                      IP: <strong>{telemetry.last_router_sync_ip || '10.10.0.4'}</strong>
                    </span>
                    <span className="telemetry-tag ok" title="Справочник объектов">
                      ✓ Справочник постов роутера синхронизирован
                    </span>
                  </div>
                </>
              ) : (
                <div className="telemetry-empty">
                  Синхронизация справочника с роутером еще не зафиксирована
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Модальное окно разрешения коллизии SMS */}
      {selectedConflict && (
        <div className="conflict-modal-overlay" onClick={() => setSelectedConflict(null)}>
          <div className="conflict-modal" onClick={(e) => e.stopPropagation()}>
            <div className="conflict-modal-header">
              <h3>
                <span>⚠️</span>
                <span>Разрешение коллизии SMS #{selectedConflict.id}</span>
              </h3>
              <button
                type="button"
                className="btn-close-modal"
                onClick={() => setSelectedConflict(null)}
              >
                ✕
              </button>
            </div>

            <div className="conflict-modal-body">
              {/* Информация об объекте */}
              <div className="conflict-object-banner">
                <div className="conflict-object-title">
                  Пост охраны: <strong>[{selectedConflict.object_code || selectedConflict.code}] {selectedConflict.object_name}</strong>
                </div>
                {selectedConflict.branch_name && (
                  <span className="branch-badge">🏢 {selectedConflict.branch_name}</span>
                )}
              </div>

              {/* Сравнение двух сторон */}
              <div className="conflict-compare-grid">
                {/* Левая колонка: Текущая открытая смена */}
                <div className="conflict-side-card conflict-side-current">
                  <div className="conflict-side-title">
                    <span>🟢</span> Текущая открытая смена
                  </div>
                  {selectedConflict.existing_shift ? (
                    <>
                      <div className="conflict-side-info-row">
                        Сотрудник: <strong>{selectedConflict.existing_shift.employee_name}</strong>
                      </div>
                      <div className="conflict-side-info-row">
                        Заступил: <strong>{formatDateTime(selectedConflict.existing_shift.started_at)}</strong>
                      </div>
                      {selectedConflict.existing_shift.sender_phone && (
                        <div className="conflict-side-info-row">
                          Телефон: <code>{selectedConflict.existing_shift.sender_phone}</code>
                        </div>
                      )}
                      {selectedConflict.existing_shift.notes && (
                        <div className="conflict-side-info-row" style={{ fontSize: '0.8rem', color: '#64748b' }}>
                          Примечание: {selectedConflict.existing_shift.notes}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="conflict-side-info-row" style={{ color: '#64748b' }}>
                      (Смена не найдена или уже завершена)
                    </div>
                  )}
                </div>

                {/* Правая колонка: Входящее конфликтующее SMS */}
                <div className="conflict-side-card conflict-side-incoming">
                  <div className="conflict-side-title">
                    <span>📩</span> Новое поступившее SMS
                  </div>
                  <div className="conflict-side-info-row">
                    Сотрудник: <strong>{selectedConflict.employee_name}</strong>
                  </div>
                  <div className="conflict-side-info-row">
                    Время SMS: <strong>{formatDateTime(selectedConflict.sms_time)}</strong>
                  </div>
                  {selectedConflict.sender_phone && (
                    <div className="conflict-side-info-row">
                      Отправитель: <code>{selectedConflict.sender_phone}</code>
                    </div>
                  )}
                  {selectedConflict.raw_sms && (
                    <div className="conflict-side-info-row">
                      Текст: <em>«{selectedConflict.raw_sms}»</em>
                    </div>
                  )}
                </div>
              </div>

              {/* Выбор действия диспетчера */}
              <div className="conflict-actions-section">
                <div className="conflict-actions-title">Выберите решение:</div>
                <div className="conflict-action-options">
                  {/* Вариант 1: Открыть по новой SMS / замена */}
                  <label
                    className={`conflict-action-card ${conflictAction === 'replace_shift' ? 'selected' : ''}`}
                    onClick={() => setConflictAction('replace_shift')}
                  >
                    <input
                      type="radio"
                      name="conflict_action"
                      value="replace_shift"
                      checked={conflictAction === 'replace_shift'}
                      onChange={() => setConflictAction('replace_shift')}
                      className="conflict-action-radio"
                    />
                    <div className="conflict-action-content">
                      <strong>🟢 Открыть смену по новой СМС (Замена / Пересменка)</strong>
                      <span className="conflict-action-desc">
                        Завершить текущую смену {selectedConflict.existing_shift?.employee_name || ''} и открыть новую смену для {selectedConflict.employee_name}.
                      </span>
                    </div>
                  </label>

                  {/* Вариант 2: Отбросить SMS */}
                  <label
                    className={`conflict-action-card ${conflictAction === 'discard_sms' ? 'selected' : ''}`}
                    onClick={() => setConflictAction('discard_sms')}
                  >
                    <input
                      type="radio"
                      name="conflict_action"
                      value="discard_sms"
                      checked={conflictAction === 'discard_sms'}
                      onChange={() => setConflictAction('discard_sms')}
                      className="conflict-action-radio"
                    />
                    <div className="conflict-action-content">
                      <strong>🔴 Отбросить СМС (Сохранить текущую смену)</strong>
                      <span className="conflict-action-desc">
                        Оставить текущую смену активной. Входящее SMS будет помечено как отклонённое/ложное.
                      </span>
                    </div>
                  </label>

                  {/* Вариант 3: Ошибочный код */}
                  <label
                    className={`conflict-action-card ${conflictAction === 'recode' ? 'selected' : ''}`}
                    onClick={() => setConflictAction('recode')}
                  >
                    <input
                      type="radio"
                      name="conflict_action"
                      value="recode"
                      checked={conflictAction === 'recode'}
                      onChange={() => setConflictAction('recode')}
                      className="conflict-action-radio"
                    />
                    <div className="conflict-action-content">
                      <strong>🟡 Ошибочный код: открыть смену по другому коду</strong>
                      <span className="conflict-action-desc">
                        Охранник ошибся в коде поста. Текущая смена на этом объекте останется нетронутой, а смена для {selectedConflict.employee_name} откроется на правильном объекте.
                      </span>

                      {conflictAction === 'recode' && (
                        <div className="recode-input-box" onClick={(e) => e.stopPropagation()}>
                          <label>Введите правильный код объекта:</label>
                          <input
                            type="text"
                            className="recode-input"
                            placeholder="Например, 0145"
                            value={recodeTargetCode}
                            maxLength={10}
                            onChange={(e) => setRecodeTargetCode(e.target.value)}
                            autoFocus
                          />

                          {recodeTargetInfo && !recodeTargetInfo.found && (
                            <div className="recode-alert-box recode-alert-notfound">
                              <span className="recode-alert-icon">❌</span>
                              <span>Объект с кодом <strong>{recodeTargetInfo.code}</strong> не найден в справочнике.</span>
                            </div>
                          )}

                          {recodeTargetInfo && recodeTargetInfo.found && (
                            recodeTargetInfo.activeShift ? (
                              <div className="recode-alert-box recode-alert-shift-active">
                                <div className="recode-alert-header">
                                  <span className="recode-alert-icon">⚠️</span>
                                  <strong>Внимание! На объекте [{recodeTargetInfo.code}] «{recodeTargetInfo.object.name}» уже открыта смена!</strong>
                                </div>
                                <div className="recode-alert-body">
                                  <div>Сотрудник на объекте: <strong>{recodeTargetInfo.activeShift.employee_name}</strong></div>
                                  <div>Заступил: <strong>{formatDateTime(recodeTargetInfo.activeShift.started_at)}</strong></div>
                                  {recodeTargetInfo.activeShift.sender_phone && (
                                    <div>Телефон: <strong>{recodeTargetInfo.activeShift.sender_phone}</strong></div>
                                  )}
                                </div>
                                <div className="recode-alert-consequence">
                                  ⚡ При применении текущая смена <strong>{recodeTargetInfo.activeShift.employee_name}</strong> будет завершена, и откроется новая смена для <strong>{selectedConflict.employee_name}</strong>.
                                </div>
                              </div>
                            ) : (
                              <div className="recode-alert-box recode-alert-free">
                                <div className="recode-alert-header">
                                  <span className="recode-alert-icon">✅</span>
                                  <span>Объект найден: <strong>[{recodeTargetInfo.code}] «{recodeTargetInfo.object.name}»</strong> ({recodeTargetInfo.object.branch_name || 'Без филиала'})</span>
                                </div>
                                <div className="recode-alert-body">
                                  Активных смен нет — смена будет открыта для <strong>{selectedConflict.employee_name}</strong>.
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      )}
                    </div>
                  </label>
                </div>
              </div>

              {/* Комментарий диспетчера */}
              <div className="conflict-comment-box">
                <label>Комментарий диспетчера (необязательно):</label>
                <input
                  type="text"
                  className="conflict-comment-input"
                  placeholder="Причина решения или отметка для журнала..."
                  value={conflictComment}
                  onChange={(e) => setConflictComment(e.target.value)}
                />
              </div>
            </div>

            <div className="conflict-modal-footer">
              <div className="conflict-nav-buttons">
                {conflicts.length > 1 && (
                  <>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => {
                        const curIdx = conflicts.findIndex((c) => c.id === selectedConflict.id);
                        const prevIdx = (curIdx - 1 + conflicts.length) % conflicts.length;
                        setSelectedConflict(conflicts[prevIdx]);
                      }}
                      title="Предыдущая коллизия"
                    >
                      ← Предыдущая
                    </button>
                    <span style={{ fontSize: '0.85rem', color: '#64748b', alignSelf: 'center' }}>
                      {conflicts.findIndex((c) => c.id === selectedConflict.id) + 1} из {conflicts.length}
                    </span>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => {
                        const curIdx = conflicts.findIndex((c) => c.id === selectedConflict.id);
                        const nextIdx = (curIdx + 1) % conflicts.length;
                        setSelectedConflict(conflicts[nextIdx]);
                      }}
                      title="Следующая коллизия"
                    >
                      Следующая →
                    </button>
                  </>
                )}
              </div>

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setSelectedConflict(null)}
                  disabled={submittingConflict}
                >
                  Отмена
                </button>
                <button
                  type="button"
                  className="btn-conflict-submit"
                  onClick={handleResolveConflict}
                  disabled={submittingConflict}
                >
                  {submittingConflict ? 'Сохранение...' : 'Применить решение'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 4. Модальное окно разрешения спорного/подозрительного SMS */}
      {selectedSuspicious && (
        <div className="conflict-modal-overlay" onClick={() => setSelectedSuspicious(null)}>
          <div className="conflict-modal" onClick={(e) => e.stopPropagation()}>
            <div className="conflict-modal-header" style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <div className="conflict-title-wrap">
                <span className="conflict-title-icon">📩</span>
                <div>
                  <h3 style={{ margin: 0, color: '#1e293b' }}>
                    Разбор спорного SMS #{selectedSuspicious.id}
                  </h3>
                  <span className="conflict-subtitle">
                    Подозрение на ошибку ввода кода или формата заступления
                  </span>
                </div>
              </div>
              <button
                type="button"
                className="conflict-modal-close"
                onClick={() => setSelectedSuspicious(null)}
              >
                ✕
              </button>
            </div>

            <div className="conflict-modal-body">
              {/* Карточка входящего спорного SMS */}
              <div className="suspicious-raw-card">
                <div className="suspicious-raw-header">
                  <span className="suspicious-label">Исходный текст входящего SMS:</span>
                  <span className="suspicious-time">{formatDateTime(selectedSuspicious.sms_time)}</span>
                </div>
                <div className="suspicious-raw-text">
                  «{selectedSuspicious.raw_sms}»
                </div>
                <div className="suspicious-meta-grid">
                  <div>📱 Номер отправителя: <strong>{selectedSuspicious.sender_phone || 'Не указан'}</strong></div>
                  <div>
                    ❓ Причина: <strong>
                      {selectedSuspicious.reason === 'code_not_found' && 'Код объекта не найден в справочнике'}
                      {selectedSuspicious.reason === 'bad_syntax' && 'Нестандартный формат текста (найдены 4 цифры)'}
                      {selectedSuspicious.reason === 'inactive_object' && 'Объект законсервирован / неактивен'}
                      {selectedSuspicious.reason === 'delayed_queue' && 'Задержка из очереди'}
                    </strong>
                  </div>
                  <div>🔢 Обнаруженный код: <strong>{selectedSuspicious.extracted_code || 'не найден'}</strong></div>
                  <div>👤 Выделенное ФИО: <strong>{selectedSuspicious.extracted_name || 'не распознано'}</strong></div>
                </div>
              </div>

              {/* Умная подсказка системы (если есть) */}
              {selectedSuspicious.history_hint && (
                <div className="suspicious-smart-hint">
                  <span className="hint-icon">💡</span>
                  <div>
                    <strong>Подсказка системы:</strong>
                    <div style={{ marginTop: '0.2rem' }}>{selectedSuspicious.history_hint}</div>
                  </div>
                </div>
              )}

              {/* Блок выбора действия */}
              <div className="conflict-actions-section">
                <h4 className="conflict-actions-title">Выберите решение диспетчера:</h4>

                <div className="conflict-actions-list">
                  {/* Действие 1: Открыть смену */}
                  <label
                    className={`conflict-action-card ${suspiciousAction === 'open_shift' ? 'selected' : ''}`}
                    onClick={() => setSuspiciousAction('open_shift')}
                  >
                    <input
                      type="radio"
                      name="suspicious_action"
                      value="open_shift"
                      checked={suspiciousAction === 'open_shift'}
                      onChange={() => setSuspiciousAction('open_shift')}
                      className="conflict-action-radio"
                    />
                    <div className="conflict-action-content">
                      <strong>🟢 Открыть смену по предложенному объекту</strong>
                      <span className="conflict-action-desc">
                        Открыть смену на объекте [
                        {selectedSuspicious.extracted_code || selectedSuspicious.suggested_object?.code || '—'}
                        ] {selectedSuspicious.suggested_object?.name || ''}.
                      </span>
                    </div>
                  </label>

                  {/* Действие 2: Исправить код / переназначить */}
                  <label
                    className={`conflict-action-card ${suspiciousAction === 'recode' ? 'selected' : ''}`}
                    onClick={() => setSuspiciousAction('recode')}
                  >
                    <input
                      type="radio"
                      name="suspicious_action"
                      value="recode"
                      checked={suspiciousAction === 'recode'}
                      onChange={() => setSuspiciousAction('recode')}
                      className="conflict-action-radio"
                    />
                    <div className="conflict-action-content">
                      <strong>🟡 Исправить код: назначить другой объект</strong>
                      <span className="conflict-action-desc">
                        Охранник ошибся в коде поста. Укажите правильный код объекта.
                      </span>

                      {suspiciousAction === 'recode' && (
                        <div className="recode-input-box" onClick={(e) => e.stopPropagation()}>
                          <label>Введите правильный код объекта:</label>
                          <input
                            type="text"
                            className="recode-input"
                            placeholder="Например, 0201"
                            value={suspiciousTargetCode}
                            maxLength={10}
                            onChange={(e) => setSuspiciousTargetCode(e.target.value)}
                            autoFocus
                          />
                        </div>
                      )}
                    </div>
                  </label>

                  {/* Действие 3: Отклонить как спам */}
                  <label
                    className={`conflict-action-card ${suspiciousAction === 'discard_spam' ? 'selected' : ''}`}
                    onClick={() => setSuspiciousAction('discard_spam')}
                  >
                    <input
                      type="radio"
                      name="suspicious_action"
                      value="discard_spam"
                      checked={suspiciousAction === 'discard_spam'}
                      onChange={() => setSuspiciousAction('discard_spam')}
                      className="conflict-action-radio"
                    />
                    <div className="conflict-action-content">
                      <strong>🔴 Отклонить сообщение (Спам / Ошибка)</strong>
                      <span className="conflict-action-desc">
                        Сообщение не относится к заступлению охраны (реклама, ошибочный номер, спам).
                      </span>
                    </div>
                  </label>
                </div>
              </div>

              {/* Статус целевого объекта (если выбран open_shift или recode) */}
              {suspiciousAction !== 'discard_spam' && (
                <div style={{ marginTop: '1rem' }}>
                  {suspiciousTargetInfo && !suspiciousTargetInfo.found && (
                    <div className="recode-alert-box recode-alert-notfound">
                      <span className="recode-alert-icon">❌</span>
                      <span>Объект с кодом <strong>{suspiciousTargetInfo.code}</strong> не найден в справочнике.</span>
                    </div>
                  )}

                  {suspiciousTargetInfo && suspiciousTargetInfo.found && (
                    suspiciousTargetInfo.activeShift ? (
                      <div className="recode-alert-box recode-alert-shift-active">
                        <div className="recode-alert-header">
                          <span className="recode-alert-icon">⚠️</span>
                          <strong>Внимание! На объекте [{suspiciousTargetInfo.code}] «{suspiciousTargetInfo.object.name}» уже открыта смена!</strong>
                        </div>
                        <div className="recode-alert-body">
                          <div>Сотрудник на объекте: <strong>{suspiciousTargetInfo.activeShift.employee_name}</strong></div>
                          <div>Заступил: <strong>{formatDateTime(suspiciousTargetInfo.activeShift.started_at)}</strong></div>
                        </div>
                        <div className="recode-alert-consequence">
                          ⚡ При сохранении текущая смена <strong>{suspiciousTargetInfo.activeShift.employee_name}</strong> будет завершена, и откроется новая смена для <strong>{suspiciousEmployeeName || selectedSuspicious.extracted_name || 'сотрудника'}</strong>.
                        </div>
                      </div>
                    ) : (
                      <div className="recode-alert-box recode-alert-free">
                        <div className="recode-alert-header">
                          <span className="recode-alert-icon">✅</span>
                          <span>Целевой объект: <strong>[{suspiciousTargetInfo.code}] «{suspiciousTargetInfo.object.name}»</strong> ({suspiciousTargetInfo.object.branch_name || 'Без филиала'})</span>
                        </div>
                        <div className="recode-alert-body">
                          Объект свободен — смена будет открыта для <strong>{suspiciousEmployeeName || selectedSuspicious.extracted_name || 'сотрудника'}</strong>.
                        </div>
                      </div>
                    )
                  )}

                  {/* Поле ФИО сотрудника */}
                  <div className="conflict-comment-box" style={{ marginTop: '0.85rem' }}>
                    <label>ФИО сотрудника для смены:</label>
                    <input
                      type="text"
                      className="conflict-comment-input"
                      placeholder="ФИО сотрудника..."
                      value={suspiciousEmployeeName}
                      onChange={(e) => setSuspiciousEmployeeName(e.target.value)}
                    />
                  </div>
                </div>
              )}

              {/* Комментарий диспетчера */}
              <div className="conflict-comment-box">
                <label>Комментарий диспетчера (необязательно):</label>
                <input
                  type="text"
                  className="conflict-comment-input"
                  placeholder="Отметка для журнала..."
                  value={suspiciousComment}
                  onChange={(e) => setSuspiciousComment(e.target.value)}
                />
              </div>
            </div>

            <div className="conflict-modal-footer">
              <div className="conflict-nav-buttons">
                {suspiciousList.length > 1 && (
                  <>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => {
                        const curIdx = suspiciousList.findIndex((s) => s.id === selectedSuspicious.id);
                        const prevIdx = (curIdx - 1 + suspiciousList.length) % suspiciousList.length;
                        const prev = suspiciousList[prevIdx];
                        setSelectedSuspicious(prev);
                        setSuspiciousTargetCode(prev.extracted_code || prev.suggested_object?.code || '');
                        setSuspiciousEmployeeName(prev.extracted_name || '');
                      }}
                      title="Предыдущее спорное SMS"
                    >
                      ← Предыдущее
                    </button>
                    <span style={{ fontSize: '0.85rem', color: '#64748b', alignSelf: 'center' }}>
                      {suspiciousList.findIndex((s) => s.id === selectedSuspicious.id) + 1} из {suspiciousList.length}
                    </span>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => {
                        const curIdx = suspiciousList.findIndex((s) => s.id === selectedSuspicious.id);
                        const nextIdx = (curIdx + 1) % suspiciousList.length;
                        const next = suspiciousList[nextIdx];
                        setSelectedSuspicious(next);
                        setSuspiciousTargetCode(next.extracted_code || next.suggested_object?.code || '');
                        setSuspiciousEmployeeName(next.extracted_name || '');
                      }}
                      title="Следующее спорное SMS"
                    >
                      Следующее →
                    </button>
                  </>
                )}
              </div>

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setSelectedSuspicious(null)}
                  disabled={submittingSuspicious}
                >
                  Отмена
                </button>
                <button
                  type="button"
                  className="btn-conflict-submit"
                  onClick={handleResolveSuspicious}
                  disabled={submittingSuspicious}
                >
                  {submittingSuspicious ? 'Сохранение...' : 'Применить решение'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
